import { useEffect, useState, useCallback, useRef } from "react";
import { Upload, Trash2, Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { useCommand, useRealtime } from "@/context/CommandContext";
import { Panel, Field, inputCls, CCButton, EmptyState, LoadingState, KpiCard, StatusBadge } from "@/command/ccUi";

const CATEGORIES = ["General", "Legal", "Finance", "HR", "Contracts", "Compliance", "Product"];
const fmtSize = (b) => (b == null ? "—" : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function Documents() {
  const { can } = useCommand();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const canCreate = can("documents", "CREATE") || can("documents", "MANAGE");
  const canDelete = can("documents", "DELETE") || can("documents", "MANAGE");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/cc/documents").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useRealtime(["documents"], load);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", modal.name || file.name);
      fd.append("category", modal.category || "General");
      await api.post("/cc/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); setModal(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/cc/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename || d.name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const del = async (id) => { if (!window.confirm("Delete this document?")) return; await api.delete(`/cc/documents/${id}`); toast.success("Deleted"); load(); };

  const byCat = CATEGORIES.map((c) => ({ c, n: rows.filter((r) => r.category === c).length })).filter((x) => x.n);

  return (
    <div>
      <h1 className="display text-2xl md:text-3xl font-light text-ink">Documents</h1>
      <p className="text-subtle mt-1 mb-6">Secure private document store — uploads never exposed publicly.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Documents" value={rows.length} />
        <KpiCard label="Categories" value={byCat.length} />
        <KpiCard label="Total Size" value={fmtSize(rows.reduce((a, r) => a + (r.size || 0), 0))} empty={rows.length === 0} />
        <KpiCard label="Latest" value={rows[0] ? new Date(rows[0].created_at).toLocaleDateString() : "—"} empty={rows.length === 0} />
      </div>

      <Panel testid="documents-panel"
        action={canCreate && <CCButton onClick={() => setModal({ category: "General" })} testid="document-add"><Upload className="w-4 h-4" /> Upload</CCButton>}>
        {loading ? <LoadingState /> : rows.length === 0 ? (
          <EmptyState title="No documents yet" hint={canCreate ? "Upload your first document." : undefined} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-subtle border-b border-hairline/[0.08]">
                <th className="px-3 py-3 font-medium">Name</th><th className="px-3 py-3 font-medium">Category</th>
                <th className="px-3 py-3 font-medium">Type</th><th className="px-3 py-3 font-medium">Size</th>
                <th className="px-3 py-3 font-medium">Uploaded by</th><th className="px-3 py-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-hairline/[0.05] text-ink" data-testid={`document-row-${r.id}`}>
                    <td className="px-3 py-3"><div className="flex items-center gap-2"><FileText className="w-4 h-4 text-subtle" /> {r.name}</div></td>
                    <td className="px-3 py-3"><StatusBadge status="info" label={r.category} /></td>
                    <td className="px-3 py-3 uppercase text-xs text-subtle">{r.ext}</td>
                    <td className="px-3 py-3">{fmtSize(r.size)}</td>
                    <td className="px-3 py-3 text-subtle">{r.uploaded_by || "—"}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <button onClick={() => download(r)} className="text-subtle hover:text-ink p-1.5" data-testid={`document-download-${r.id}`}><Download className="w-4 h-4" /></button>
                      {canDelete && <button onClick={() => del(r.id)} className="text-subtle hover:text-red-500 p-1.5" data-testid={`document-del-${r.id}`}><Trash2 className="w-4 h-4" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => !busy && setModal(null)} data-testid="document-modal">
          <div className="glass-heavy rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="display text-lg font-medium text-ink mb-4">Upload document</h3>
            <div className="grid gap-4">
              <Field label="Name (optional)"><input className={inputCls} value={modal.name || ""} onChange={(e) => setModal({ ...modal, name: e.target.value })} data-testid="document-field-name" /></Field>
              <Field label="Category">
                <select className={inputCls} value={modal.category} onChange={(e) => setModal({ ...modal, category: e.target.value })} data-testid="document-field-category">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="File"><input ref={fileRef} type="file" className={inputCls} data-testid="document-field-file" /></Field>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => !busy && setModal(null)} className="text-sm text-subtle hover:text-ink px-4 py-2">Cancel</button>
              <CCButton onClick={upload} disabled={busy} testid="document-upload-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload</CCButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
